import React, { useState } from 'react';
import { Modal, IconButton, CircularProgress, FormLabel, FormHelperText } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { feedbackModalStyles as styles } from './feedbackModal.tailwind.js';
import InputField from '../../Input/InputField/InputField';
import TextArea from '../../Input/TextArea/TextArea';
import OverlayLoader from '../../Loaders/OverlayLoader/OverlayLoader';
import { useDispatch, useSelector } from 'react-redux';
import { submitUserFeedback } from '../../../../redux/slices/feedbackSlice';
import { showErrorSnackbar } from '../../../../redux/slices/snackbarSlice';
import { useEffect } from 'react';
import feedbackIcon from '../../../../assets/svgs/feedback.svg';

const validateEmail = (email) => {
    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
};

const FeedbackModal = ({ isOpen, onClose }) => {
    const { user, isSignedIn } = useSelector((state) => state.user);
    const { isLoading } = useSelector((state) => state.feedback);
    const profileDetails = user?.userDetails;

    const [formData, setFormData] = useState({
        name: '',
        userEmail: '',
        userPhone: '',
        feedback: ''
    });
    const dispatch = useDispatch();

    // Autofill name, email, and phone if user is logged in
    useEffect(() => {
        if (isOpen && isSignedIn && profileDetails) {
            setFormData(prev => ({
                ...prev,
                name: `${profileDetails?.userFirstName || ''} ${profileDetails?.userLastName || ''}`.trim() || profileDetails?.userNickName || '',
                userEmail: profileDetails?.userEmail || '',
                userPhone: profileDetails?.userCellNumber || ''
            }));
        }
    }, [isOpen, isSignedIn, profileDetails]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isContactMethodProvided = isSignedIn
            ? (formData.userEmail.trim() || formData.userPhone.trim())
            : formData.userEmail.trim();

        if (!formData.name.trim() || !formData.feedback.trim() || !isContactMethodProvided) {
            dispatch(showErrorSnackbar({ title: 'Please fill in all required fields' }));
            return;
        }
        try {
            const submitData = {
                name: formData.name,
                userEmail: formData.userEmail,
                userPhone: formData.userPhone,
                feedback: formData.feedback,
            };

            const resultAction = await dispatch(submitUserFeedback(submitData));

            // only close the modal if successful

            if (submitUserFeedback.fulfilled.match(resultAction)) {
                setFormData(prev => ({ ...prev, feedback: '' }));
                onClose();
            }
        } catch (error) {
            console.error('Feedback submission error:', error);
        }
    };

    const isContactMethodProvided = isSignedIn
        ? (formData.userEmail.trim() || formData.userPhone.trim())
        : (formData.userEmail.trim() && validateEmail(formData.userEmail.trim()));

    const isFeedbackValid = formData.feedback.trim().length >= 1;
    const isDisabled = isLoading || !formData.name.trim() || !isContactMethodProvided || !isFeedbackValid;

    const emailError = !isSignedIn && formData.userEmail.trim() && !validateEmail(formData.userEmail.trim());
    const feedbackError = false; 

    return (
        <>
            <Modal open={isOpen} onClose={onClose} disableAutoFocus>
                <div className={styles.container}>
                    {isLoading && <OverlayLoader />}
                    {/* Header */}
                    <div className={styles.header}>
                        <img src={feedbackIcon} alt="feedback" className={styles.feedbackIcon} />
                        <h2 className={styles.title}>Send Us Your Feedback</h2>
                        <button onClick={onClose} className={styles.closeButton}>
                            <CloseIcon sx={{ fontSize: "16px" }} />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className={styles.form}>
                        {!isSignedIn && (
                            <>
                                <div className={styles.inputWrapper}>
                                    <FormLabel sx={styles.muiLabel}>Name <span className="text-primaryHeading">*</span></FormLabel>
                                    <InputField
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Your name"
                                        sx={styles.muiInputProps.sx}
                                        showLabel={false}
                                    />
                                </div>

                                <div className={styles.inputWrapper}>
                                    <FormLabel sx={styles.muiLabel}>Email <span className="text-primaryHeading">*</span></FormLabel>
                                    <InputField
                                        name="userEmail"
                                        type="email"
                                        value={formData.userEmail}
                                        onChange={handleInputChange}
                                        placeholder="example@mail.com"
                                        sx={styles.muiInputProps.sx}
                                        showLabel={false}
                                        error={emailError}
                                        helperText={emailError ? "Please enter a valid email address" : ""}
                                    />
                                </div>
                            </>
                        )}

                        <div className={styles.inputWrapper}>
                            {!isSignedIn && <FormLabel sx={styles.muiLabel}>Feedback <span className="text-primaryHeading">*</span></FormLabel>}
                            <div className={styles.textAreaWrapper(feedbackError)}>
                                <TextArea
                                    name="feedback"
                                    value={formData.feedback}
                                    onChange={handleInputChange}
                                    placeholder="Tell us what you think..."
                                    background="transparent"
                                    color="black"
                                    placeholderColor="#8C8C8C"
                                    rows={4}
                                    className={styles.textArea}
                                />
                            </div>

                        </div>

                        {/* Footer Buttons */}
                        <div className={styles.footerButtons}>
                            <button
                                type="button"
                                onClick={onClose}
                                className={styles.cancelButton}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.submitButton(isDisabled)}
                                disabled={isDisabled}
                            >
                                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Feedback'}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </>
    );
};

export default FeedbackModal;
